<template>
  <div class="band-table-wrap">
    <table class="band-table">
      <thead>
        <tr>
          <th class="left">Group</th>
          <th class="left">Position</th>
          <th class="left">Band</th>
          <th>Base</th>
          <th>Allowance</th>
          <th>Total package</th>
          <th>Staff</th>
          <th>FTE</th>
          <th>2026 avg package<span class="th-note">same staff</span></th>
          <th>vs 2026</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="grp in grouped" :key="grp.name">
          <tr v-for="(b, i) in grp.bands" :key="grp.name + b.position + b.band" :class="{ 'group-start': i === 0 }">
            <td class="left group-cell">{{ i === 0 ? grp.name : '' }}</td>
            <td class="left">{{ b.position }}</td>
            <td class="left band-cell">
              {{ b.band }}
              <span v-if="b.steps2026 && b.steps2026.length" class="steps-hint">from {{ b.steps2026.join(', ') }}</span>
            </td>
            <td>{{ fmt(b.base) }}</td>
            <td>{{ fmt(b.allowance) }}</td>
            <td class="strong">{{ fmt(b.package) }}</td>
            <td>{{ b.staffOnBand || '—' }}</td>
            <td>{{ b.fteOnBand || '—' }}</td>
            <td>{{ b.avgPackage2026 ? fmt(b.avgPackage2026) : '—' }}</td>
            <td class="delta-cell" :class="{ 'delta-hot': isHot(b) }">{{ delta(b) }}</td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
const GROUP_ORDER = ['Leadership', 'Teacher', 'Teaching Assistant', 'Admin', 'Ancillary', 'Counsellor'];

function groupOf(band, isLeadership) {
  if (isLeadership) return 'Leadership';
  const p = band.position.toLowerCase();
  if (p.startsWith('teacher')) return 'Teacher';
  if (p.startsWith('teaching assistant')) return 'Teaching Assistant';
  if (p.startsWith('ancillary')) return 'Ancillary';
  if (p.includes('counsellor')) return 'Counsellor';
  return 'Admin';
}

export default {
  name: 'BandTable',
  props: {
    staffBands: { type: Array, required: true },
    leadershipBands: { type: Array, required: true },
  },
  computed: {
    grouped() {
      const buckets = {};
      for (const b of this.leadershipBands) {
        (buckets[groupOf(b, true)] = buckets[groupOf(b, true)] || []).push(b);
      }
      for (const b of this.staffBands) {
        (buckets[groupOf(b, false)] = buckets[groupOf(b, false)] || []).push(b);
      }
      return GROUP_ORDER.filter((g) => buckets[g]).map((g) => ({
        name: g,
        bands: buckets[g].slice().sort((a, b) => b.package - a.package),
      }));
    },
  },
  methods: {
    fmt(v) { return Math.round(v).toLocaleString('en-US'); },
    delta(b) {
      if (!b.avgPackage2026) return '—';
      const d = (b.package / b.avgPackage2026 - 1) * 100;
      return `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`;
    },
    isHot(b) {
      return b.avgPackage2026 && (b.package / b.avgPackage2026 - 1) * 100 >= 25;
    },
  },
};
</script>

<style scoped>
.band-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.band-table { width: 100%; border-collapse: collapse; font-size: 12.5px; color: var(--ink); min-width: 860px; }
th {
  text-align: right; font-weight: 600; color: var(--muted); font-size: 10px;
  letter-spacing: 0.08em; text-transform: uppercase;
  padding: 6px 10px; border-bottom: 1.5px solid var(--ink); white-space: nowrap;
}
.th-note { display: block; font-weight: 400; letter-spacing: 0; text-transform: none; font-size: 10px; }
td {
  text-align: right; padding: 6.5px 10px; border-bottom: 1px solid var(--hairline);
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
tr.group-start td { border-top: 1.5px solid var(--ink); }
.left { text-align: left; }
.group-cell { font-weight: 700; }
.strong { font-weight: 700; }
.band-cell { color: var(--ink); }
.steps-hint { display: block; color: var(--muted); font-size: 10px; }
.delta-cell { font-weight: 700; }
.delta-hot { color: var(--red); }
tbody tr:hover td { background: var(--track); }
</style>
